import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { blogPosts, blogCategories } from '@/mocks/blogData';

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = useMemo(() => {
    let posts = blogPosts;
    if (activeCategory !== 'All') {
      posts = posts.filter((p) => p.category === activeCategory || p.tags.includes(activeCategory));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      posts = posts.filter((p) =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return posts;
  }, [activeCategory, searchQuery]);

  const featuredPost = blogPosts.find((p) => p.featured) || blogPosts[0];
  const regularPosts = filteredPosts.filter((p) => p.id !== featuredPost.id);

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <section className="relative py-20 md:py-28 bg-foreground-950 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-900/40 via-foreground-900 to-accent-900/30"></div>
          <div className="absolute top-10 left-10 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-accent-500/10 rounded-full blur-3xl"></div>
          <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <span className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm text-accent-300 text-xs font-semibold rounded-full mb-4">
              Stories & Updates
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Stories That <span className="gradient-text">Inspire</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-8">
              Real stories of transformation, impact updates, and voices from the communities we serve across India.
            </p>

            <div className="max-w-lg mx-auto relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
              <input
                type="text"
                placeholder="Search articles, topics, tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white placeholder-white/40 text-sm focus:outline-none focus:border-accent-400/50 focus:bg-white/15 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                >
                  <i className="ri-close-line text-white/60 text-xs"></i>
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="py-6 md:py-8 bg-background-50 border-b border-background-200/60">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {blogCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeCategory === cat
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 border border-background-200/70'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            {!searchQuery && activeCategory === 'All' && (
              <div className="mb-12 md:mb-16">
                <span className="text-accent-600 text-xs font-semibold uppercase tracking-wider mb-3 block">Featured Story</span>
                <div className="group grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 bg-background-100 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-200/50 transition-all duration-300">
                  <div className="relative aspect-video lg:aspect-auto lg:min-h-[400px] overflow-hidden">
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-primary-500 text-white text-xs font-semibold rounded-full">{featuredPost.category}</span>
                    </div>
                  </div>
                  <div className="p-6 lg:p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={featuredPost.authorImage}
                        alt={featuredPost.author}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-foreground-900 text-sm font-semibold">{featuredPost.author}</p>
                        <p className="text-foreground-400 text-xs">{featuredPost.authorRole}</p>
                      </div>
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3 group-hover:text-primary-600 transition-colors leading-tight">
                      {featuredPost.title}
                    </h2>
                    <p className="text-foreground-500 text-sm md:text-base leading-relaxed mb-5">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mb-5">
                      {featuredPost.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="px-2.5 py-1 bg-background-200/70 text-foreground-600 text-[11px] font-medium rounded-md">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-foreground-400 text-xs">
                        <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{featuredPost.date}</span>
                        <span className="flex items-center gap-1"><i className="ri-time-line"></i>{featuredPost.readTime}</span>
                      </div>
                      <Link
                        to={`/blog/${featuredPost.id}`}
                        className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-700 text-sm font-semibold group/link whitespace-nowrap"
                      >
                        Read Story
                        <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularPosts.map((post) => (
                <article
                  key={post.id}
                  className="group bg-background-100 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-200/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 bg-white/90 backdrop-blur-sm text-foreground-700 text-[11px] font-semibold rounded-full">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-2 mb-4">
                      {post.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-background-200/70 text-foreground-500 text-[10px] font-medium rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-background-200/60">
                      <div className="flex items-center gap-2">
                        <img
                          src={post.authorImage}
                          alt={post.author}
                          className="w-7 h-7 rounded-full object-cover"
                        />
                        <div>
                          <p className="text-foreground-700 text-xs font-medium">{post.author}</p>
                          <p className="text-foreground-400 text-[10px]">{post.readTime}</p>
                        </div>
                      </div>
                      <span className="text-foreground-400 text-[10px]">{post.date}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filteredPosts.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-article-line text-foreground-300 text-2xl"></i>
                </div>
                <p className="text-foreground-400 text-sm mb-2">No articles match your search.</p>
                <button
                  onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}